import cn from 'classnames'
import { useEffect } from 'react'

import { DropdownItem } from './dropdown-item'
import { BaseItem, RenderItem } from './types'
import { useVirtual } from './use-virtual'

import styles from './dropdown-list.module.scss'

type DropdownListProps<T extends BaseItem> = {
  id

  items: T[]

  activeIndex: number

  selectedItem?: T

  loading: boolean

  error: unknown

  className?: string

  itemClassName?: string

  emptyComponent?: React.ReactNode

  loadingComponent?: React.ReactNode

  itemHeight?: number

  errorComponent?: (error: unknown) => React.ReactNode

  renderItem?: RenderItem<T>

  onItemHover: (index: number) => void

  onItemClick: (item: T) => void

  onScroll?: React.UIEventHandler<HTMLDivElement>
}

export function DropdownList<T extends BaseItem>({
  id,
  items,
  activeIndex,
  selectedItem,
  loading,
  error,
  className,
  itemClassName,
  emptyComponent,
  loadingComponent,
  itemHeight,
  errorComponent,
  renderItem,
  onItemHover,
  onItemClick,
  onScroll,
}: DropdownListProps<T>) {
  const { parentRef, virtualItems, totalSize, virtualizer } = useVirtual({
    count: items.length,
    estimateSize: itemHeight,
  })

  useEffect(() => {
    if (items.length === 0) {
      return
    }

    if (activeIndex >= 0) {
      virtualizer.scrollToIndex(activeIndex, {
        align: 'auto',
      })
    }
  }, [activeIndex, virtualizer, items.length])

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    onScroll?.(event)
  }

  if (loading && items.length === 0) {
    return <div className={styles.state}>{loadingComponent ?? 'Загрузка...'}</div>
  }

  if (error) {
    return (
      <div className={styles.state}>
        {errorComponent ? errorComponent(error) : 'Не удалось загрузить данные'}
      </div>
    )
  }

  if (!loading && items.length === 0) {
    return <div className={styles.state}>{emptyComponent ?? 'Ничего не найдено'}</div>
  }

  return (
    <div
      ref={parentRef}
      id={`${id}-list`}
      role="listbox"
      onScroll={handleScroll}
      className={cn(styles.root, className)}
    >
      <div
        style={{
          height: totalSize,
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualRow) => {
          const item = items[virtualRow.index]
          if (!item) {
            return null
          }

          return (
            <div
              key={item.id}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <DropdownItem
                id={`${id}-item-${virtualRow.index}`}
                item={item}
                active={virtualRow.index === activeIndex}
                selected={selectedItem?.id === item.id}
                className={itemClassName}
                renderItem={renderItem}
                onMouseEnter={() => onItemHover(virtualRow.index)}
                onClick={() => onItemClick(item)}
                itemHeight={itemHeight}
              />
            </div>
          )
        })}
      </div>

      {loading && <div className={styles.loading}>{loadingComponent ?? 'Загрузка...'}</div>}
    </div>
  )
}
